import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from "lucide-react";
import {
  api,
  type Block,
  type DialerScript,
  type Step,
  type Template,
} from "@/lib/api";
import {
  Badge,
  Button,
  Card,
  Checkbox,
  ConfirmDialog,
  Dialog,
  Empty,
  ErrorNote,
  Field,
  Input,
  Notice,
  PageHeader,
  Select,
  SortableTh,
  Spinner,
  Table,
  TableFooter,
  Tabs,
  Td,
  Textarea,
  Th,
  Toolbar,
} from "@/components/ui";
import { useClientTable } from "@/lib/table";
import { dateModeLabel } from "./companies";

export function DefinitionsPage() {
  const [tab, setTab] = useState("templates");

  return (
    <>
      <PageHeader
        title="Ansagen-Definitionen"
        description="Woraus eine Ansage zusammengesetzt wird: Bausteine, Baupläne und die Skripte des Roboters."
      />

      <Tabs
        active={tab}
        onChange={setTab}
        tabs={[
          { id: "templates", label: "Baupläne" },
          { id: "blocks", label: "Bausteine" },
          { id: "scripts", label: "Dialer-Skripte" },
        ]}
      />

      {tab === "templates" && <Templates />}
      {tab === "blocks" && <Blocks />}
      {tab === "scripts" && <Scripts />}
    </>
  );
}

// --- building blocks -----------------------------------------------------

function Blocks() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Block | null>(null);
  const [removing, setRemoving] = useState<Block | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["blocks"],
    queryFn: () => api<Block[]>("/api/admin/blocks"),
  });

  const placeholders = useQuery({
    queryKey: ["placeholders"],
    queryFn: () =>
      api<{ variable: string; placeholder: string; description: string }[]>(
        "/api/admin/blocks/placeholders",
      ),
  });

  const save = useMutation({
    mutationFn: (b: Block) =>
      b.id
        ? api<Block>(`/api/admin/blocks/${b.id}`, { method: "PUT", body: b })
        : api<Block>("/api/admin/blocks", { method: "POST", body: b }),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["blocks"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api<void>(`/api/admin/blocks/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: ["blocks"] });
    },
  });

  // The definitions are a catalogue, not a log: they arrive whole and are
  // ordered here.
  const table = useClientTable(data, {
    initial: { key: "namePattern", dir: "asc" },
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <Card className="p-4">
        <Toolbar>
          <Button
            variant="primary"
            onClick={() =>
              setEditing({
                id: 0,
                namePattern: "",
                description: "",
                variable: "none",
                usedBy: 0,
              })
            }
          >
            <Plus className="size-4" aria-hidden /> Baustein anlegen
          </Button>
        </Toolbar>

        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="namePattern">
                Dateimuster
              </SortableTh>
              <SortableTh table={table} sortKey="description">
                Beschreibung
              </SortableTh>
              <SortableTh table={table} sortKey="variable">
                Auflösung
              </SortableTh>
              <SortableTh table={table} sortKey="usedBy" first="desc" align="right">
                verwendet in
              </SortableTh>
              <Th />
            </tr>
          </thead>
          <tbody>
            {table.rows.map((b) => (
              <tr key={b.id}>
                <Td className="font-mono text-xs">{b.namePattern}</Td>
                <Td>{b.description || "—"}</Td>
                <Td>
                  <Badge>{b.variable}</Badge>
                </Td>
                <Td className="text-right tabular-nums">{b.usedBy}</Td>
                <Td className="text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Baustein bearbeiten"
                    title="Baustein bearbeiten"
                    onClick={() => setEditing(b)}
                  >
                    <Pencil className="size-4" aria-hidden />
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={b.usedBy > 0}
                    title={b.usedBy > 0 ? "Noch in Bauplänen verwendet" : undefined}
                    onClick={() => setRemoving(b)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {(data ?? []).length === 0 && <Empty title="Noch keine Bausteine" />}
        <TableFooter table={table} noun="Bausteine" />
      </Card>

      {editing && (
        <Dialog
          open
          onClose={() => setEditing(null)}
          title={editing.id ? "Baustein bearbeiten" : "Baustein anlegen"}
        >
          <BlockForm
            value={editing}
            placeholders={placeholders.data ?? []}
            onCancel={() => setEditing(null)}
            onSave={(b) => save.mutate(b)}
            busy={save.isPending}
            error={save.error as Error | null}
          />
        </Dialog>
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Baustein löschen"
        message={
          <>
            <strong className="font-mono">{removing?.namePattern}</strong> wird
            gelöscht. Die Aufnahmen auf der Platte bleiben.
          </>
        }
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
        busy={remove.isPending}
      />
    </>
  );
}

function BlockForm({
  value,
  placeholders,
  onCancel,
  onSave,
  busy,
  error,
}: {
  value: Block;
  placeholders: { variable: string; placeholder: string; description: string }[];
  onCancel: () => void;
  onSave: (b: Block) => void;
  busy: boolean;
  error: Error | null;
}) {
  const [draft, setDraft] = useState(value);
  const set = <K extends keyof Block>(k: K, v: Block[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const placeholder = placeholders.find((p) => p.variable === draft.variable);

  return (
    <div className="flex flex-col gap-4">
      <Field
        label="Dateimuster"
        hint="Der Name der Aufnahme ohne Endung, mit Platzhaltern. Kein Pfad."
      >
        <Input
          className="font-mono"
          value={draft.namePattern}
          onChange={(e) => set("namePattern", e.target.value)}
        />
      </Field>
      <Field label="Beschreibung">
        <Input
          value={draft.description}
          onChange={(e) => set("description", e.target.value)}
        />
      </Field>
      <Field label="Auflösung">
        <Select value={draft.variable} onChange={(e) => set("variable", e.target.value)}>
          <option value="none">fester Text (kein Platzhalter)</option>
          {placeholders.map((p) => (
            <option key={p.variable} value={p.variable}>
              {p.placeholder} — {p.description}
            </option>
          ))}
        </Select>
      </Field>

      {placeholder && (
        <Notice>
          Das Muster muss <code className="font-mono">{placeholder.placeholder}</code>{" "}
          enthalten, sonst würde jede Ansage dieselbe Aufnahme verwenden.
        </Notice>
      )}

      {error && <ErrorNote>{error.message}</ErrorNote>}
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button variant="primary" disabled={busy} onClick={() => onSave(draft)}>
          Speichern
        </Button>
      </div>
    </div>
  );
}

// --- blueprints ----------------------------------------------------------

function Templates() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Template | null>(null);
  const [removing, setRemoving] = useState<Template | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["templates"],
    queryFn: () => api<Template[]>("/api/admin/templates"),
  });
  const blocks = useQuery({
    queryKey: ["blocks"],
    queryFn: () => api<Block[]>("/api/admin/blocks"),
  });

  const save = useMutation({
    mutationFn: (t: Template) =>
      t.id
        ? api<Template>(`/api/admin/templates/${t.id}`, { method: "PUT", body: t })
        : api<Template>("/api/admin/templates", { method: "POST", body: t }),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api<void>(`/api/admin/templates/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: ["templates"] });
    },
  });

  const table = useClientTable(data, {
    initial: { key: "name", dir: "asc" },
    accessors: {
      steps: (t) => t.steps.length,
      dateMode: (t) => dateModeLabel(t.dateMode),
      proxyRequired: (t) => t.proxyRequired,
    },
  });

  if (isLoading) return <Spinner />;

  return (
    <>
      <Card className="p-4">
        <Toolbar>
          <Button
            variant="primary"
            onClick={() =>
              setEditing({
                id: 0,
                name: "",
                description: "",
                steps: [],
                proxyRequired: false,
                callType: "record",
                dateMode: "end_required",
                proxySource: "",
                notifyAllowed: true,
                fallbackSuffix: "",
                usedBy: 0,
              })
            }
          >
            <Plus className="size-4" aria-hidden /> Bauplan anlegen
          </Button>
        </Toolbar>

        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="name">
                Name
              </SortableTh>
              <SortableTh table={table} sortKey="steps" first="desc">
                Blöcke
              </SortableTh>
              <SortableTh table={table} sortKey="dateMode">
                Datum
              </SortableTh>
              <SortableTh table={table} sortKey="proxyRequired" first="desc">
                Vertretung
              </SortableTh>
              <SortableTh table={table} sortKey="usedBy" first="desc" align="right">
                verwendet von
              </SortableTh>
              <Th />
            </tr>
          </thead>
          <tbody>
            {table.rows.map((t) => (
              <tr key={t.id}>
                <Td className="font-medium">{t.name}</Td>
                <Td className="muted">{t.steps.length}</Td>
                <Td>{dateModeLabel(t.dateMode)}</Td>
                <Td>{t.proxyRequired ? "nötig" : "—"}</Td>
                <Td className="text-right tabular-nums">{t.usedBy}</Td>
                <Td className="text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Bauplan bearbeiten"
                    title="Bauplan bearbeiten"
                    onClick={() => setEditing(t)}
                  >
                    <Pencil className="size-4" aria-hidden />
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={t.usedBy > 0}
                    onClick={() => setRemoving(t)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {(data ?? []).length === 0 && <Empty title="Noch keine Baupläne" />}
        <TableFooter table={table} noun="Baupläne" />
      </Card>

      {editing && (
        <TemplateDialog
          value={editing}
          blocks={blocks.data ?? []}
          onClose={() => setEditing(null)}
          onSave={(t) => save.mutate(t)}
          busy={save.isPending}
          error={save.error as Error | null}
        />
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Bauplan löschen"
        message={<><strong>{removing?.name}</strong> wird gelöscht.</>}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
        busy={remove.isPending}
      />
    </>
  );
}

function TemplateDialog({
  value,
  blocks,
  onClose,
  onSave,
  busy,
  error,
}: {
  value: Template;
  blocks: Block[];
  onClose: () => void;
  onSave: (t: Template) => void;
  busy: boolean;
  error: Error | null;
}) {
  const [draft, setDraft] = useState(value);
  const set = <K extends keyof Template>(k: K, v: Template[K]) =>
    setDraft((d) => ({ ...d, [k]: v }));

  const setSteps = (steps: Step[]) => set("steps", steps);
  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= draft.steps.length) return;
    const next = [...draft.steps];
    const moved = next[i]!;
    next[i] = next[j]!;
    next[j] = moved;
    setSteps(next);
  };

  const byId = new Map(blocks.map((b) => [b.id, b]));

  return (
    <Dialog open onClose={onClose} title={value.id ? "Bauplan bearbeiten" : "Bauplan anlegen"}>
      <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto">
        <Field label="Name">
          <Input value={draft.name} onChange={(e) => set("name", e.target.value)} />
        </Field>
        <Field label="Beschreibung">
          <Input
            value={draft.description}
            onChange={(e) => set("description", e.target.value)}
          />
        </Field>

        <div>
          <p className="mb-2 text-sm font-medium">Blockfolge</p>
          {draft.steps.length === 0 && (
            <p className="mb-2 text-sm muted">
              Noch keine Blöcke. Ein Bauplan ohne Blöcke ergäbe Stille.
            </p>
          )}
          <div className="flex flex-col gap-1.5">
            {draft.steps.map((step, i) => {
              const block = byId.get(step.blockId);
              // The parameter is the value the legacy form wrote in braces,
              // "9{30}". It is the pause length, and a block is a pause when
              // its pattern carries [SILENCE] — the renderer decides by the
              // pattern, not by the declared kind, so the form does the same.
              // Any step may carry one, and one that is set is always shown:
              // a hidden value is a value nobody can remove.
              const isPause =
                block?.variable === "silence" ||
                (block?.namePattern ?? "").toUpperCase().includes("[SILENCE]");
              const showParam = isPause || step.param !== undefined;
              const setParam = (param: number | undefined) => {
                const next = [...draft.steps];
                const rest = { ...step };
                delete rest.param;
                next[i] = param === undefined ? rest : { ...rest, param };
                setSteps(next);
              };
              return (
                <div key={i} className="flex items-center gap-2">
                  <span className="w-6 text-right text-xs muted">{i + 1}.</span>
                  <Select
                    value={step.blockId}
                    onChange={(e) => {
                      const next = [...draft.steps];
                      next[i] = { ...step, blockId: Number(e.target.value) };
                      setSteps(next);
                    }}
                  >
                    {blocks.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.description || b.namePattern}
                      </option>
                    ))}
                  </Select>
                  {showParam ? (
                    <Input
                      className="w-32"
                      type="number"
                      min={0}
                      max={1500}
                      placeholder="{ } 1/100 s"
                      title={
                        isPause
                          ? "Pause in Hundertstelsekunden: 40 = 0,4 Sekunden"
                          : "Wert in geschweiften Klammern. Wirkt nur bei Blöcken mit [SILENCE] im Muster; leer lassen entfernt ihn."
                      }
                      value={step.param ?? ""}
                      onChange={(e) =>
                        setParam(
                          e.target.value === "" ? undefined : Number(e.target.value),
                        )
                      }
                    />
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="font-mono"
                      title="Wert in geschweiften Klammern hinzufügen, wie 9{30} im Altsystem"
                      onClick={() => setParam(0)}
                    >
                      {"{ }"}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => move(i, -1)}>
                    <ArrowUp className="size-4" aria-hidden />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => move(i, 1)}>
                    <ArrowDown className="size-4" aria-hidden />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setSteps(draft.steps.filter((_, j) => j !== i))}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </div>
              );
            })}
          </div>
          <Button
            className="mt-2"
            size="sm"
            onClick={() =>
              setSteps([...draft.steps, { blockId: blocks[0]?.id ?? 0 }])
            }
            disabled={blocks.length === 0}
          >
            <Plus className="size-4" aria-hidden /> Block hinzufügen
          </Button>
        </div>

        {draft.legacyBlocks && (
          <Notice>
            Ursprüngliche Schreibweise aus dem Altsystem:{" "}
            <code className="font-mono">{draft.legacyBlocks}</code>
          </Notice>
        )}

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Datumseingabe">
            <Select value={draft.dateMode} onChange={(e) => set("dateMode", e.target.value)}>
              <option value="end_required">Enddatum nötig</option>
              <option value="end_optional">Enddatum optional</option>
              <option value="minutes_only">nur Minuten (Seminar)</option>
              <option value="end_with_time">Enddatum mit Uhrzeit</option>
            </Select>
          </Field>
          <Field label="Anrufart" hint="Ob die Aufnahme mitgeschnitten werden darf.">
            <Select
              value={draft.callType}
              onChange={(e) => set("callType", e.target.value as Template["callType"])}
            >
              <option value="record">Audio_rec</option>
              <option value="no_record">Audio_Norec</option>
            </Select>
          </Field>
          <Field label="Vertretung aus" hint="Leer: was die Firma sagt.">
            <Select
              value={draft.proxySource}
              onChange={(e) => set("proxySource", e.target.value)}
            >
              <option value="">von der Firma</option>
              <option value="users">Mitarbeitern</option>
              <option value="secretariats">Sekretariaten</option>
            </Select>
          </Field>
          <Field
            label="Fallback-Suffix"
            hint="Ergänzt die generische Aufnahme, z. B. _krank."
          >
            <Input
              value={draft.fallbackSuffix}
              onChange={(e) => set("fallbackSuffix", e.target.value)}
            />
          </Field>
        </div>

        <div>
          <Checkbox
            label="Vertretung erforderlich"
            checked={draft.proxyRequired}
            onChange={(e) => set("proxyRequired", e.target.checked)}
          />
          <Checkbox
            label="Benachrichtigung möglich"
            checked={draft.notifyAllowed}
            onChange={(e) => set("notifyAllowed", e.target.checked)}
          />
        </div>

        {error && <ErrorNote>{error.message}</ErrorNote>}
        <div className="flex justify-end gap-2">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" disabled={busy} onClick={() => onSave(draft)}>
            Speichern
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

// --- dialer scripts ------------------------------------------------------

function Scripts() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<DialerScript | null>(null);
  const [removing, setRemoving] = useState<DialerScript | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["dialer-scripts"],
    queryFn: () => api<DialerScript[]>("/api/admin/dialer-scripts"),
  });

  const save = useMutation({
    mutationFn: (s: DialerScript) =>
      s.id
        ? api<DialerScript>(`/api/admin/dialer-scripts/${s.id}`, { method: "PUT", body: s })
        : api<DialerScript>("/api/admin/dialer-scripts", { method: "POST", body: s }),
    onSuccess: () => {
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["dialer-scripts"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) =>
      api<void>(`/api/admin/dialer-scripts/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setRemoving(null);
      void qc.invalidateQueries({ queryKey: ["dialer-scripts"] });
    },
  });

  const table = useClientTable(data, { initial: { key: "name", dir: "asc" } });

  if (isLoading) return <Spinner />;

  return (
    <>
      <Card className="p-4">
        <Toolbar>
          <Button
            variant="primary"
            onClick={() => setEditing({ id: 0, name: "", body: "", usedBy: 0 })}
          >
            <Plus className="size-4" aria-hidden /> Skript anlegen
          </Button>
        </Toolbar>

        <Table>
          <thead>
            <tr>
              <SortableTh table={table} sortKey="name">
                Name
              </SortableTh>
              <SortableTh table={table} sortKey="usedBy" first="desc" align="right">
                Benutzer
              </SortableTh>
              <Th />
            </tr>
          </thead>
          <tbody>
            {table.rows.map((s) => (
              <tr key={s.id}>
                <Td className="font-medium">{s.name}</Td>
                <Td className="text-right tabular-nums">{s.usedBy}</Td>
                <Td className="text-right">
                  <Button
                    size="sm"
                    variant="ghost"
                    aria-label="Skript bearbeiten"
                    title="Skript bearbeiten"
                    onClick={() => setEditing(s)}
                  >
                    <Pencil className="size-4" aria-hidden />
                  </Button>{" "}
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={s.usedBy > 0}
                    onClick={() => setRemoving(s)}
                  >
                    <Trash2 className="size-4" aria-hidden />
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
        {(data ?? []).length === 0 && <Empty title="Noch keine Skripte" />}
        <TableFooter table={table} noun="Skripte" />
      </Card>

      {editing && (
        <Dialog
          open
          onClose={() => setEditing(null)}
          title={editing.id ? "Skript bearbeiten" : "Skript anlegen"}
        >
          <ScriptForm
            value={editing}
            onCancel={() => setEditing(null)}
            onSave={(s) => save.mutate(s)}
            busy={save.isPending}
            error={save.error as Error | null}
          />
        </Dialog>
      )}

      <ConfirmDialog
        open={removing !== null}
        title="Skript löschen"
        message={<><strong>{removing?.name}</strong> wird gelöscht.</>}
        onConfirm={() => removing && remove.mutate(removing.id)}
        onClose={() => setRemoving(null)}
        busy={remove.isPending}
      />
    </>
  );
}

function ScriptForm({
  value,
  onCancel,
  onSave,
  busy,
  error,
}: {
  value: DialerScript;
  onCancel: () => void;
  onSave: (s: DialerScript) => void;
  busy: boolean;
  error: Error | null;
}) {
  const [draft, setDraft] = useState(value);
  return (
    <div className="flex flex-col gap-4">
      <Field label="Name">
        <Input
          value={draft.name}
          onChange={(e) => setDraft({ ...draft, name: e.target.value })}
        />
      </Field>
      <Field
        label="Skript"
        hint="JavaScript, das mbrobotctl während des Anrufs ausführt."
      >
        <Textarea
          className="min-h-64 font-mono text-xs"
          value={draft.body}
          onChange={(e) => setDraft({ ...draft, body: e.target.value })}
        />
      </Field>
      {error && <ErrorNote>{error.message}</ErrorNote>}
      <div className="flex justify-end gap-2">
        <Button onClick={onCancel}>Abbrechen</Button>
        <Button variant="primary" disabled={busy} onClick={() => onSave(draft)}>
          Speichern
        </Button>
      </div>
    </div>
  );
}
